import { useLocalStorageWithSubscription } from "@sundaeswap/react-hooks";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useCallback, useMemo } from "react";

export type TWithdrawStatus =
  | "requesting"   // L2 submission in progress
  | "approved"     // L2 accepted the withdrawal
  | "settling"     // Waiting for L1 settlement
  | "settled"      // Settlement tx detected on L1
  | "failed";      // Processing error

export interface IWithdrawHistoryTx {
  status: TWithdrawStatus;
  /** Human-readable amount label, e.g. "10 ADA" */
  amountLabel: string;
  /** Unix timestamp (seconds) when the withdrawal was submitted */
  submittedAt: number;
  /** L1 settlement tx hash, if detected */
  settlementTxHash?: string;
  errorMessage?: string;
}

export type TWithdrawMap = Map<string, IWithdrawHistoryTx>;

const EMPTY_MAP: TWithdrawMap = new Map();

function readFromStorage(key: string): TWithdrawMap {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Map();
    return new Map(JSON.parse(raw));
  } catch {
    return new Map();
  }
}

function writeToStorage(key: string, map: TWithdrawMap) {
  localStorage.setItem(key, JSON.stringify([...map.entries()]));
}

let nextWithdrawId = Date.now();

export function generateWithdrawId(): string {
  return `w-${nextWithdrawId++}`;
}

export const useWithdrawTxs = () => {
  const { mainAddress } = useWalletObserver();

  const storageKey = useMemo(() => {
    if (!mainAddress) return null;
    return `withdrawTxs_${mainAddress.slice(-12)}`;
  }, [mainAddress]);

  const [stored, setStored, clearStored] =
    useLocalStorageWithSubscription<TWithdrawMap>(
      storageKey ?? "withdrawTxs_unused",
      new Map(),
      {
        serializer(value) {
          return JSON.stringify([...value.entries()]);
        },
        deserializer(value) {
          return new Map(JSON.parse(value));
        },
      },
    );

  const active = storageKey !== null;
  const withdrawTxs = active ? stored : EMPTY_MAP;

  const addWithdrawTx = useCallback(
    (id: string, data: IWithdrawHistoryTx) => {
      if (!storageKey) {
        throw new Error("addWithdrawTx called without a connected wallet");
      }
      const current = readFromStorage(storageKey);
      current.set(id, data);
      writeToStorage(storageKey, current);
      setStored(current);
    },
    [storageKey, setStored],
  );

  const updateWithdrawTx = useCallback(
    (id: string, data: Partial<IWithdrawHistoryTx>) => {
      if (!storageKey) {
        throw new Error("updateWithdrawTx called without a connected wallet");
      }
      const current = readFromStorage(storageKey);
      const existing = current.get(id);
      if (!existing) return;
      current.set(id, { ...existing, ...data });
      writeToStorage(storageKey, current);
      setStored(current);
    },
    [storageKey, setStored],
  );

  return {
    withdrawTxs,
    addWithdrawTx,
    updateWithdrawTx,
    clearWithdrawTxs: clearStored,
  };
};
